import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Popup,
  ToolbarItem
} from 'devextreme-react/popup';
import { SaveButton } from '../save-button/save-button';

const DRAWER_POSITION = { my: 'right top', at: 'right top', of: window };

// Lets a nested SquirePopup tell its parent SquirePopup that a child is open,
// so the parent can disable its own Save while the user is editing in the child.
const NestedPopupContext = createContext(null);


/**
 * SquirePopup - A reusable popup component with save/cancel functionality
 *
 * mode='drawer' (default): anchors to the right edge of the screen, full viewport height.
 *   Use for all primary edit forms.
 * mode='popup': centered floating dialog. Use for small utility dialogs and sub-editors.
 *
 * nested=true: this popup stages changes into a parent editor's DTO rather than
 *   persisting to the server. Defaults the action label to "Done", suppresses the
 *   dirty-yellow indicator, and disables the parent SquirePopup's Save while open.
 *
 * @param {Function} onSave - Called when save button is clicked.
 *   - If it returns a rejected Promise, popup stays open
 *   - If it returns a resolved Promise (or throws no error), popup closes
 */
export function SquirePopup({
  visible = false,
  title,
  onClose,
  onSave,
  children,
  mode = 'drawer',
  width,
  height,
  maxHeight,
  maxWidth,
  dragEnabled = false,
  hideOnOutsideClick = false,
  showCloseButton = true,
  showTitle = true,
  additionalToolbarItems = [],
  saveButtonText,
  saveButtonType, // accepted for API compatibility; deliberately not forwarded to the popup
  saveButtonStylingMode = "outlined",
  saveButtonDisabled = false,
  saveButtonValidationGroup = "validation-group",
  isDirty = false,
  nested = false,
  cancelButtonText = "Cancel",
  cancelButtonType = "default",
  cancelButtonStylingMode = "outlined",
  ...popupProps
}) {
  const isDrawer = mode === 'drawer';

  const resolvedWidth = width ?? (isDrawer ? 'calc(100vw - 250px)' : '80vw');
  const resolvedHeight = isDrawer ? '100vh' : (height ?? '80vh');
  const resolvedMaxHeight = isDrawer ? '100vh' : (maxHeight ?? '90vh');
  const resolvedMaxWidth = isDrawer ? resolvedWidth : (maxWidth ?? '95vw');
  const position = isDrawer ? DRAWER_POSITION : undefined;

  const resolvedSaveText = saveButtonText ?? (nested ? 'Done' : 'Save');
  const effectiveDirty = nested ? false : isDirty;

  // Track open nested popups so we can disable our own Save while a child is up.
  const [nestedOpenCount, setNestedOpenCount] = useState(0);
  const nestedApi = useMemo(() => ({
    register: () => setNestedOpenCount(c => c + 1),
    unregister: () => setNestedOpenCount(c => Math.max(0, c - 1)),
  }), []);

  // If we're a nested popup, register/unregister with the closest parent SquirePopup.
  const parentNested = useContext(NestedPopupContext);
  useEffect(() => {
    if (!nested || !parentNested || !visible) return undefined;
    parentNested.register();
    return () => parentNested.unregister();
  }, [nested, parentNested, visible]);

  const effectiveSaveDisabled = saveButtonDisabled || nestedOpenCount > 0;

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Memoize render function to prevent DevExtreme ToolbarItem from re-mounting
  // the SaveButton on every parent render.
  const saveButtonRender = useCallback(() => (
    <SaveButton
      text={resolvedSaveText}
      stylingMode={saveButtonStylingMode}
      validationGroup={saveButtonValidationGroup}
      disabled={effectiveSaveDisabled}
      isDirty={effectiveDirty}
      onSave={onSave}
      onSaved={handleClose}
    />
  ), [resolvedSaveText, saveButtonStylingMode, saveButtonValidationGroup,
      effectiveSaveDisabled, effectiveDirty, onSave, handleClose]);

  const cancelButtonOptions = useMemo(() => ({
    text: cancelButtonText,
    type: cancelButtonType,
    stylingMode: cancelButtonStylingMode,
    onClick: handleClose
  }), [cancelButtonText, cancelButtonType, cancelButtonStylingMode, handleClose]);

  return (
    <NestedPopupContext.Provider value={nestedApi}>
      <Popup
        visible={visible}
        title={title}
        onHiding={handleClose}
        dragEnabled={dragEnabled}
        hideOnOutsideClick={hideOnOutsideClick}
        showCloseButton={showCloseButton}
        showTitle={showTitle}
        position={position}
        width={resolvedWidth}
        height={resolvedHeight}
        maxHeight={resolvedMaxHeight}
        maxWidth={resolvedMaxWidth}
        focusStateEnabled={false}
        tabIndex={-1}
        {...popupProps}
      >
        {children}

        {additionalToolbarItems.map((item, index) => (
          <ToolbarItem
            key={index}
            widget="dxButton"
            toolbar={item.toolbar || "bottom"}
            location={item.location || "after"}
            options={item.options}
          />
        ))}

        <ToolbarItem
          toolbar="bottom"
          location="after"
          render={saveButtonRender}
        />

        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={cancelButtonOptions}
        />
      </Popup>
    </NestedPopupContext.Provider>
  );
}
